export const formatQuantityAsFraction = (quantity: number): string => {
  // Handle whole numbers
  if (quantity % 1 === 0) {
    return quantity.toString();
  }

  // Common cooking fractions using Unicode vulgar fraction characters
  const cookingFractions: Array<{ value: number; display: string }> = [
    { value: 0.125, display: "\u215B" },  // ⅛
    { value: 0.25, display: "\u00BC" },   // ¼
    { value: 0.333, display: "\u2153" },  // ⅓
    { value: 0.375, display: "\u215C" },  // ⅜
    { value: 0.5, display: "\u00BD" },    // ½
    { value: 0.625, display: "\u215D" },  // ⅝
    { value: 0.667, display: "\u2154" },  // ⅔
    { value: 0.75, display: "\u00BE" },   // ¾
    { value: 0.875, display: "\u215E" },  // ⅞
  ];

  // Helper function to find nearest cooking fraction
  const findNearestFraction = (value: number): string => {
    let nearest = cookingFractions[0];
    let minDiff = Math.abs(value - nearest.value);

    for (const fraction of cookingFractions) {
      const diff = Math.abs(value - fraction.value);
      if (diff < minDiff) {
        minDiff = diff;
        nearest = fraction;
      }
    }

    return nearest.display;
  };

  // Check for mixed numbers (e.g., 1.5 → 1 1/2)
  const wholePart = Math.floor(quantity);
  const decimalPart = quantity - wholePart;

  if (decimalPart < 0.001) {
    // Handle floating point precision for whole numbers
    return wholePart.toString();
  }

  // Find the nearest cooking fraction for the decimal part
  const nearestFraction = findNearestFraction(decimalPart);

  if (wholePart > 0) {
    return `${wholePart} ${nearestFraction}`;
  }

  return nearestFraction;
};

export const formatQuantity = (quantity: number, unit: string): string => {
  const formattedQuantity = formatQuantityAsFraction(quantity);

  // Handle plural forms and abbreviations
  const unitLower = unit.toLowerCase();
  const unitMap: {
    [key: string]: { singular: string; plural: string; abbrev?: string };
  } = {
    // Common units that might come in singular or plural
    pound: { singular: "lb", plural: "lbs" },
    pounds: { singular: "lb", plural: "lbs" },
    ounce: { singular: "oz", plural: "oz" },
    ounces: { singular: "oz", plural: "oz" },
    tablespoon: { singular: "tbsp", plural: "tbsp" },
    tablespoons: { singular: "tbsp", plural: "tbsp" },
    teaspoon: { singular: "tsp", plural: "tsp" },
    teaspoons: { singular: "tsp", plural: "tsp" },
    dash: { singular: "dash", plural: "dashes" },
    pinch: { singular: "pinch", plural: "pinches" },
    cup: { singular: "cup", plural: "cups" },
    cups: { singular: "cup", plural: "cups" },
    clove: { singular: "clove", plural: "cloves" },
    cloves: { singular: "clove", plural: "cloves" },
    slice: { singular: "slice", plural: "slices" },
    slices: { singular: "slice", plural: "slices" },
    piece: { singular: "piece", plural: "pieces" },
    pieces: { singular: "piece", plural: "pieces" },
    can: { singular: "can", plural: "cans" },
    cans: { singular: "can", plural: "cans" },
    jar: { singular: "jar", plural: "jars" },
    jars: { singular: "jar", plural: "jars" },
    bottle: { singular: "bottle", plural: "bottles" },
    bottles: { singular: "bottle", plural: "bottles" },
    package: { singular: "package", plural: "packages" },
    packages: { singular: "package", plural: "packages" },
    bunch: { singular: "bunch", plural: "bunches" },
    bunches: { singular: "bunch", plural: "bunches" },
    head: { singular: "head", plural: "heads" },
    heads: { singular: "head", plural: "heads" },
    each: { singular: "", plural: "" }, // Handle "each" units - always show count
  };

  const unitInfo = unitMap[unitLower];
  if (unitInfo) {
    // For "each" units, don't show the word "each", just the count
    if (unitLower === "each") {
      return formattedQuantity;
    }
    const displayUnit =
      unitInfo.abbrev ||
      (quantity > 0 && quantity <= 1 ? unitInfo.singular : unitInfo.plural);
    return `${formattedQuantity} ${displayUnit}`;
  }

  // Default case - just use the unit as provided
  return `${formattedQuantity} ${unit}`;
};

/**
 * Map of ASCII fraction strings to Unicode vulgar fraction characters.
 */
const asciiFractionMap: Record<string, string> = {
  "1/8": "\u215B",  // ⅛
  "1/4": "\u00BC",  // ¼
  "1/3": "\u2153",  // ⅓
  "3/8": "\u215C",  // ⅜
  "1/2": "\u00BD",  // ½
  "5/8": "\u215D",  // ⅝
  "2/3": "\u2154",  // ⅔
  "3/4": "\u00BE",  // ¾
  "7/8": "\u215E",  // ⅞
};

/**
 * Replaces ASCII fractions in text with Unicode vulgar fraction characters.
 * Handles both standalone fractions ("1/2 cup") and mixed numbers ("1 1/2 cups").
 * @param text - The text to process
 * @returns Text with ASCII fractions replaced by Unicode glyphs
 */
export const displayFractions = (text: string): string => {
  // Match optional whole number + space + fraction (e.g. "1 1/2" or "1/2")
  return text.replace(/(\d+)\s+(\d+\/\d+)/g, (match, whole, frac) => {
    const unicode = asciiFractionMap[frac];
    return unicode ? `${whole} ${unicode}` : match;
  }).replace(/(\d+\/\d+)/g, (match) => {
    return asciiFractionMap[match] || match;
  });
};
